import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  MessageSquare,
  Paperclip,
  Send,
  Clock,
  Link2,
  User,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Trash2,
  Pencil,
  ChevronDown,
  X,
  XCircle,
  ImageIcon,
  FileText,
  Archive,
  Plus,
  Info,
  Bell,
  Download,
} from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { hasPermission } from '../lib/permissions';
import { useRealtimeTasks } from '../hooks/useRealtime';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EmptyState } from '../components/common/EmptyState';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import TaskModal from '../components/tasks/TaskModal';
import ReminderModal from '../components/reminders/ReminderModal';
import FilePreview from '../components/files/FilePreview';
import type { Task, SubTask, TaskComment, FileItem, User as UserType, UserGroup } from '../types';

const STATUS_LABELS: Record<string, string> = {
  atandi: 'Atandı',
  beklemede: 'Beklemede',
  yapiliyor: 'Yapılıyor',
  tamamlandi: 'Tamamlandı',
  iptal_edildi: 'İptal',
  arsivlendi: 'Arşivlendi',
};

const STATUS_BADGE: Record<string, string> = {
  atandi: 'bg-blue-100 text-blue-700',
  beklemede: 'bg-yellow-100 text-yellow-700',
  yapiliyor: 'bg-blue-100 text-blue-700',
  tamamlandi: 'bg-green-100 text-green-700',
  iptal_edildi: 'bg-red-100 text-red-700',
  arsivlendi: 'bg-slate-100 text-slate-700',
};

const DEPENDENCY_TYPE_LABELS: Record<string, string> = {
  'finish-to-start': 'Bitiş → Başlangıç',
  'start-to-start': 'Başlangıç → Başlangıç',
  'finish-to-finish': 'Bitiş → Bitiş',
  'start-to-finish': 'Başlangıç → Bitiş',
};

const PRIORITY_LABELS: Record<string, string> = {
  dusuk: 'Düşük',
  orta: 'Orta',
  yuksek: 'Yüksek',
  acil: 'Acil',
};

const PRIORITY_BADGE: Record<string, string> = {
  dusuk: 'bg-slate-100 text-slate-600',
  orta: 'bg-blue-100 text-blue-700',
  yuksek: 'bg-orange-100 text-orange-700',
  acil: 'bg-red-100 text-red-700',
};

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  yonetici: 'Yönetici',
  sef: 'Şef',
  muhendis: 'Mühendis',
  kullanici: 'Kullanıcı',
};

interface AuditLog {
  id: number;
  user_name: string;
  action: string;
  created_at: string;
}

interface DependencyItem {
  id: number;
  type: string;
  depends_on_task?: { id: number; title: string; status: string };
  task?: { id: number; title: string; status: string };
}

const DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

const sortCommentsAsc = (list: TaskComment[]) =>
  [...list].sort(
    (a, b) => new Date(a.created_at + 'Z').getTime() - new Date(b.created_at + 'Z').getTime(),
  );

// API tarihleri iki formatta gelebiliyor: "2026-07-12 17:45:00" (UTC, Z'siz)
// veya ISO 8601 ("2026-07-12T17:45:00.000000Z"). Z'siz formata UTC damgası ekle.
const toApiDate = (s: string) => new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + 'Z');

const downloadTaskFile = async (f: FileItem) => {
  try {
    const res = await api.get(`/api/v1/files/${f.id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = f.original_name || f.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch {
    window.alert('Dosya indirilemedi.');
  }
};

function SharedImageThumb({ file, onOpen }: { file: FileItem; onOpen: (f: FileItem) => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    api
      .get(`/api/v1/files/${file.id}/view`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file.id]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(file)}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(file)}
      data-testid={`shared-image-${file.id}`}
      className="group relative block cursor-pointer overflow-hidden rounded-lg border border-slate-200 bg-white text-left hover:border-blue-300"
    >
      {src && !failed ? (
        <img src={src} alt={file.original_name || file.name} className="h-28 w-full object-cover sm:h-36" />
      ) : (
        <div className="flex h-28 w-full items-center justify-center bg-slate-50 sm:h-36">
          {failed ? (
            <ImageIcon className="h-8 w-8 text-slate-300" />
          ) : (
            <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
          )}
        </div>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          downloadTaskFile(file);
        }}
        data-testid={`shared-image-download-${file.id}`}
        title="İndir"
        aria-label="Görüntüyü indir"
        className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-lg bg-black/50 text-white opacity-80 hover:bg-black/70"
      >
        <Download className="h-4 w-4" />
      </button>
      <div className="min-w-0 px-2 py-1.5">
        <p className="truncate text-[11px] font-medium text-slate-700 sm:text-xs">{file.original_name || file.name}</p>
        <p className="truncate text-[10px] text-slate-500 sm:text-[11px]">
          {file.owner?.name ? `${file.owner.name} · ` : ''}
          {file.created_at ? toApiDate(file.created_at).toLocaleString('tr-TR', DATE_TIME_FORMAT) : ''}
        </p>
      </div>
    </div>
  );
}

export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [task, setTask] = useState<Task | null>(null);
  const [subTasks, setSubTasks] = useState<SubTask[]>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [dependencies, setDependencies] = useState<DependencyItem[]>([]);
  const [dependents, setDependents] = useState<DependencyItem[]>([]);
  const [assignees, setAssignees] = useState<UserType[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'comments' | 'history' | 'dependencies' | 'files'>('comments');
  const [commentText, setCommentText] = useState('');
  const [commentAttachments, setCommentAttachments] = useState<File[]>([]);
  const [sendingComment, setSendingComment] = useState(false);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingComment, setDeletingComment] = useState(false);
  const [deleteCommentTarget, setDeleteCommentTarget] = useState<number | null>(null);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [selectedDependencyTaskId, setSelectedDependencyTaskId] = useState('');
  const [selectedDependencyType, setSelectedDependencyType] = useState('finish-to-start');
  const [addingDependency, setAddingDependency] = useState(false);
  const [dependencyError, setDependencyError] = useState<string | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<UserType | null>(null);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const isArchived = task?.is_archived || task?.status === 'arsivlendi';

  const isTaskAssignee = useMemo(() => {
    if (!task || !user) return false;
    const inUsers = task.assigned_users?.some((u) => u.id === user.id);
    const inGroups = task.assigned_groups?.some((g) => g.users?.some((u) => u.id === user.id));
    return !!(inUsers || inGroups);
  }, [task, user]);

  const eligibleReminderRecipients = useMemo(() => {
    const map = new Map<number, UserType>();
    if (task?.creator) map.set(task.creator.id, task.creator as unknown as UserType);
    task?.assigned_users?.forEach((u) => map.set(u.id, u as unknown as UserType));
    task?.assigned_groups?.forEach((g) => {
      g.users?.forEach((u) => map.set(u.id, u as unknown as UserType));
    });
    return Array.from(map.values());
  }, [task]);

  // İçerik düzenleme: sadece yetkili roller (admin/yönetici/sef/mühendis) ve görev sahibi.
  const canEditTask = !!(task && user && !task.is_readonly && !task.is_archived && !task.is_pending_approval && hasPermission(user, 'tasks', 'update'));

  // Durum değiştirme: yetkili rollerin yanı sıra göreve atanmış herkes (sorumlu/yardımcı/grup üyesi).
  const canChangeStatus = !!(task && user && !task.is_readonly && !task.is_archived && !task.is_pending_approval && (hasPermission(user, 'tasks', 'update') || isTaskAssignee));

  const canReopenTask = !!(
    task &&
    user &&
    task.status === 'tamamlandi' &&
    task.approval_status === 'approved' &&
    (user.role === 'admin' || user.role === 'yonetici' || task.creator?.id === user.id)
  );

  const fetchAll = useCallback(async (signal?: AbortSignal) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [taskRes, subRes, commentRes, fileRes, logRes, depRes, usersRes, groupsRes] = await Promise.all([
        api.get(`/api/v1/tasks/${id}`, { signal }),
        api.get(`/api/v1/tasks/${id}/sub-tasks`, { signal }).catch(() => ({ data: { data: [] } })),
        api.get(`/api/v1/tasks/${id}/comments`, { signal }).catch(() => ({ data: { data: [] } })),
        api.get(`/api/v1/tasks/${id}/attachments`, { signal }).catch(() => ({ data: { data: [] } })),
        api.get(`/api/v1/tasks/${id}/history`, { signal }).catch(() => ({ data: { data: [] } })),
        api.get(`/api/v1/tasks/${id}/dependencies`, { signal }).catch(() => ({ data: { data: [] } })),
        api.get('/api/v1/users/search?search=&per_page=1000&exclude_self=false', { signal }).catch(() => ({ data: { data: [] } })),
        api.get('/api/v1/user-groups', { signal }).catch(() => ({ data: { data: [] } })),
      ]);

      const t = taskRes.data?.data || taskRes.data;
      setTask(t);
      setSubTasks(subRes.data?.data || []);
      setComments(sortCommentsAsc(commentRes.data?.data || []));
      setFiles(fileRes.data?.data || []);
      setAuditLogs(logRes.data?.data || []);
      const depData = depRes.data?.data || { dependencies: [], dependents: [] };
      setDependencies(depData.dependencies || []);
      setDependents(depData.dependents || []);
      setAssignees(t?.assignees || t?.assigned_users || []);
      setUsers(usersRes.data?.data || []);
      setGroups(groupsRes.data?.data || []);

      if (t?.project_id) {
        try {
          const projRes = await api.get(`/api/v1/projects/${t.project_id}`, { signal });
          setProjectName(projRes.data?.data?.name || null);
        } catch (err: unknown) {
          const e = err as { name?: string };
          if (e.name === 'CanceledError' || e.name === 'AbortError') {
            return;
          }
          console.error('Hata:', err);
        }
      }

      // Goruntulenen gorevi okundu olarak isaretle
      try {
        await api.post(`/api/v1/tasks/${id}/read`);
        setTask((prev) => (prev ? { ...prev, is_unread: false } : prev));
      } catch {
        // Okundu isaretleme hatasi kritik degil
      }
    } catch (err: unknown) {
      const e = err as { name?: string };
      if (e.name === 'CanceledError' || e.name === 'AbortError') {
        return;
      }
      console.error('Hata:', err);
      setError('Görev yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();
    fetchAll(controller.signal);
    return () => controller.abort();
  }, [id, fetchAll]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // showDeleteConfirm / showRejectDialog ConfirmDialog üzerinden modal
        // yığınına kayıtlı; ESC'yi kendileri işler (iç içe modalda yalnız üstteki kapanır).
        if (dependencyModalOpen) setDependencyModalOpen(false);
        if (editModalOpen) setEditModalOpen(false);
        if (selectedAssignee) setSelectedAssignee(null);
        if (reminderModalOpen) setReminderModalOpen(false);
      }
    };
    if (showDeleteConfirm || dependencyModalOpen || showRejectDialog || editModalOpen || selectedAssignee || reminderModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [showDeleteConfirm, dependencyModalOpen, showRejectDialog, editModalOpen, selectedAssignee, reminderModalOpen]);

  useRealtimeTasks(task?.project_id, {
    onTaskUpdated: (updated) => {
      if (updated.id !== Number(id)) return;
      setTask((prev) => (prev ? { ...prev, ...updated } : prev));
      setAssignees((updated.assigned_users || []).map((u) => ({ ...u, avatar: null } as UserType)));
    },
    onTaskDeleted: (taskId) => {
      if (taskId !== Number(id)) return;
      navigate('/tasks');
    },
    onTaskStatusChanged: (taskId, payload) => {
      if (taskId !== Number(id)) return;
      setTask((prev) =>
        prev
          ? {
              ...prev,
              status: payload.new_status as Task['status'],
              approval_status: payload.approval_status as Task['approval_status'],
              approved_by: payload.approved_by,
              approved_at: payload.approved_at,
            }
          : prev
      );
    },
    onSubTaskCreated: (_, subTask) => {
      if (subTask.task_id !== Number(id)) return;
      setSubTasks((prev) => (prev.some((s) => s.id === subTask.id) ? prev : [...prev, subTask]));
    },
    onSubTaskToggled: (_, subTask) => {
      if (subTask.task_id !== Number(id)) return;
      setSubTasks((prev) => prev.map((s) => (s.id === subTask.id ? subTask : s)));
    },
    onTaskCommentCreated: (_, comment) => {
      if (comment.task_id !== Number(id)) return;
      setComments((prev) => (prev.some((c) => c.id === comment.id) ? prev : [...prev, comment]));
      // Baska bir kullanici yorumla dosya eklediginde gorev ekleri listesini de guncelle.
      if (comment.has_attachment || comment.attachments?.length) {
        api.get(`/api/v1/tasks/${id}/attachments`)
          .then((res) => setFiles(res.data?.data || []))
          .catch(() => {});
      }
    },
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    if (statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownOpen]);

  const toggleSubTask = async (subId: number, current: boolean) => {
    try {
      await api.patch(`/api/v1/sub-tasks/${subId}/toggle`, { is_completed: !current });
      setSubTasks((prev) => prev.map((s) => (s.id === subId ? { ...s, is_completed: !current } : s)));
    } catch (err) {
      console.error('Hata:', err);
      setError('Alt görev güncellenirken bir hata oluştu.');
    }
  };

  const sendComment = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if ((!commentText.trim() && commentAttachments.length === 0) || sendingComment || !id) return;
    setSendingComment(true);
    setError(null);
    try {
      const formData = new FormData();
      if (commentText.trim()) formData.append('content', commentText.trim());
      commentAttachments.forEach((file) => formData.append('attachments[]', file));
      const res = await api.post(`/api/v1/tasks/${id}/comments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setComments((prev) => [...prev, res.data?.data || res.data]);
      setCommentText('');
      setCommentAttachments([]);
      // Gorev eklerini de yenile; yorumdaki dosyalar artik gorev eklerinde gorunuyor
      const fileRes = await api.get(`/api/v1/tasks/${id}/attachments`);
      setFiles(fileRes.data?.data || []);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data
          ?.message || 'Yorum gönderilirken bir hata oluştu.';
      console.error('Hata:', err);
      setError(msg);
    } finally {
      setSendingComment(false);
    }
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendComment();
    }
  };

  const handleAttachmentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      e.target.value = '';
      return;
    }
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (files.length > 10) {
      setError('En fazla 10 dosya ekleyebilirsiniz.');
      return;
    }
    const oversized = files.filter((f) => f.size > 10 * 1024 * 1024);
    if (oversized.length > 0) {
      setError('Her dosya en fazla 10 MB olabilir.');
      return;
    }
    setCommentAttachments(files);
    setError(null);
  };

  const removeAttachment = (idx: number) => {
    setCommentAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const loadAvailableTasks = useCallback(async () => {
    if (!task?.project_id) return;
    try {
      const res = await api.get('/api/v1/tasks', {
        params: { project_id: task.project_id, limit: 100 },
      });
      const allTasks: Task[] = res.data.data || [];
      const existingDependencyIds = new Set([
        ...dependencies.map((d) => d.depends_on_task?.id).filter(Boolean),
        Number(id),
      ]);
      setAvailableTasks(allTasks.filter((t) => !existingDependencyIds.has(t.id)));
    } catch (err) {
      console.error('Hata:', err);
      setDependencyError('Görevler yüklenirken bir hata oluştu.');
    }
  }, [task, dependencies, id]);

  const openDependencyModal = () => {
    setDependencyError(null);
    setSelectedDependencyTaskId('');
    setSelectedDependencyType('finish-to-start');
    setDependencyModalOpen(true);
    loadAvailableTasks();
  };

  const addDependency = async () => {
    if (!id || !selectedDependencyTaskId) return;
    setAddingDependency(true);
    setDependencyError(null);
    try {
      await api.post(`/api/v1/tasks/${id}/dependencies`, {
        depends_on_task_id: Number(selectedDependencyTaskId),
        type: selectedDependencyType,
      });
      setDependencyModalOpen(false);
      fetchAll();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Bağımlılık eklenirken bir hata oluştu.';
      setDependencyError(msg);
    } finally {
      setAddingDependency(false);
    }
  };

  const removeDependency = async (dependencyId: number) => {
    if (!id) return;
    try {
      await api.delete(`/api/v1/task-dependencies/${dependencyId}`);
      fetchAll();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Bağımlılık silinirken bir hata oluştu.';
      setError(msg);
    }
  };

  const handleDeleteTask = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      await api.delete(`/api/v1/tasks/${id}`);
      setShowDeleteConfirm(false);
      // Show toast-like success and navigate
      navigate('/tasks');
    } catch (err) {
      console.error('Hata:', err);
      setError('Görev silinirken bir hata oluştu.');
      setDeleting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!id) return;
    try {
      setDeletingComment(true);
      await api.delete(`/api/v1/tasks/${id}/comments/${commentId}`);
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? {
                ...c,
                is_deleted: true,
                deleted_at: new Date().toISOString(),
                comment: 'Bu yorum silindi',
                attachments: [],
                has_attachment: false,
                attachment_path: null,
                attachment_url: null,
                metadata: null,
              }
            : c,
        ),
      );
    } catch (err) {
      console.error('Hata:', err);
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Yorum silinirken bir hata oluştu.';
      setError(msg);
    } finally {
      setDeletingComment(false);
      setDeleteCommentTarget(null);
    }
  };

  const loadComments = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get(`/api/v1/tasks/${id}/comments`);
      setComments(sortCommentsAsc(res.data?.data || []));
    } catch {
      // Yorumlar yenilenirken hata oluşursa sessizce bırak; ana işlem etkilenmemeli.
    }
  }, [id]);

  const handleStatusChange = async (newStatus: string) => {
    if (!id || !task || task.status === newStatus) return;
    setStatusChanging(true);
    setError(null);
    setStatusDropdownOpen(false);
    try {
      const res = await api.patch(`/api/v1/tasks/${id}/status`, { status: newStatus });
      const updated = res.data?.data || res.data;
      setTask((prev) => (prev ? { ...prev, ...updated } : prev));
      await loadComments();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Durum güncellenirken bir hata oluştu.';
      console.error('Hata:', err);
      setError(msg);
    } finally {
      setStatusChanging(false);
    }
  };

  const handleReopen = async () => {
    if (!id || !task) return;
    setStatusChanging(true);
    setError(null);
    try {
      const res = await api.patch(`/api/v1/tasks/${id}/status`, { status: 'atandi' });
      const updated = res.data?.data || res.data;
      setTask((prev) => (prev ? { ...prev, ...updated } : prev));
      await loadComments();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Görev yeniden açılırken bir hata oluştu.';
      console.error('Hata:', err);
      setError(msg);
    } finally {
      setStatusChanging(false);
    }
  };

  const isOverdue = (t: Task | null) => {
    if (!t?.due_date || t.status === 'tamamlandi' || t.status === 'iptal_edildi') return false;
    const due = new Date(t.due_date);
    const today = new Date();
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return due < today;
  };

  const getStatusDisplay = (t: Task | null) => {
    if (!t) return { label: '-', badge: 'bg-slate-100 text-slate-600' };
    if (t.approval_status === 'approved') {
      return { label: 'Onaylandı', badge: 'bg-green-100 text-green-700' };
    }
    if (t.approval_status === 'rejected') {
      return { label: 'Reddedildi', badge: 'bg-red-100 text-red-700' };
    }
    if (t.status === 'tamamlandi' && t.approval_status === 'pending') {
      return { label: 'Tamamlandı (Onay Bekliyor)', badge: 'bg-yellow-100 text-yellow-700' };
    }
    return { label: STATUS_LABELS[t.status] || t.status, badge: STATUS_BADGE[t.status] || 'bg-slate-100 text-slate-600' };
  };

  const handleApprove = async () => {
    if (!id || !task) return;
    setStatusChanging(true);
    setError(null);
    try {
      const res = await api.post(`/api/v1/tasks/${id}/approve`);
      const updated = res.data?.data || res.data;
      setTask((prev) => (prev ? { ...prev, status: updated.status, approval_status: updated.approval_status, approved_by: updated.approved_by, approved_at: updated.approved_at, approved_by_user: updated.approved_by_user } : prev));
      await loadComments();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Onay işlemi sırasında bir hata oluştu.';
      console.error('Hata:', err);
      setError(msg);
    } finally {
      setStatusChanging(false);
    }
  };

  const handleReject = () => {
    if (!id || !task) return;
    setRejectReason('');
    setRejectError(null);
    setShowRejectDialog(true);
  };

  const confirmReject = async () => {
    if (!id || !task) return;
    setStatusChanging(true);
    setRejectError(null);
    try {
      const res = await api.post(`/api/v1/tasks/${id}/reject`, { reason: rejectReason });
      const updated = res.data?.data || res.data;
      setTask((prev) => (prev ? { ...prev, status: updated.status, approval_status: updated.approval_status, approved_by: updated.approved_by, approved_at: updated.approved_at, approved_by_user: updated.approved_by_user } : prev));
      await loadComments();
      // Diyalog yalnız başarıda kapanır; hata durumunda açık kalır ve hata gösterilir.
      setShowRejectDialog(false);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Reddetme işlemi sırasında bir hata oluştu.';
      console.error('Hata:', err);
      setRejectError(msg);
    } finally {
      setStatusChanging(false);
    }
  };

  const canApprove = (t: Task | null, u: UserType | null) => {
    if (!t || !u) return false;
    return (
      t.status === 'tamamlandi' &&
      t.approval_status === 'pending' &&
      (u.role === 'admin' || u.role === 'yonetici' || t.creator?.id === u.id)
    );
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!task) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Görev bulunamadı"
        description="Bu görev mevcut değil veya erişim izniniz yok."
      />
    );
  }

  const completedSubs = subTasks.filter((s) => s.is_completed).length;

  return (
    <div className="mx-auto max-w-4xl space-y-4 sm:space-y-6">
      {/* Üst bar */}
      <button
        onClick={() => navigate('/tasks')}
        data-testid="back-to-tasks-button"
        className="flex min-h-[44px] w-fit items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800"
      >
        <ArrowLeft className="h-4 w-4" />
        Görevlere Dön
      </button>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {error}
        </div>
      )}

      {/* Onay Banner */}
      {task.status === 'tamamlandi' && task.approval_status === 'pending' && (
        <div data-testid="approval-banner" className="rounded-xl border border-yellow-200 bg-yellow-50 p-3 shadow-sm sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold text-yellow-800 sm:text-sm">Bu görev tamamlandı olarak işaretlendi.</p>
              <p className="text-[10px] text-yellow-700 sm:text-xs">Görevi onaylamadan durumu “Bitti” olmaz.</p>
            </div>
            {canApprove(task, user) && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={statusChanging}
                  data-testid="reject-button"
                  className="min-h-[44px] rounded-lg border border-yellow-300 bg-white px-3 py-1.5 text-xs font-medium text-yellow-700 hover:bg-yellow-100 disabled:opacity-60 sm:px-4 sm:py-2 sm:text-sm"
                >
                  Reddet
                </button>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={statusChanging}
                  data-testid="approve-button"
                  className="flex min-h-[44px] items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-60 sm:px-4 sm:py-2 sm:text-sm"
                >
                  {statusChanging && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Onayla
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Başlık & Badges */}
      <div className="rounded-xl bg-white p-3.5 shadow-sm sm:p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative" ref={statusDropdownRef}>
            <button
              type="button"
              onClick={() => canChangeStatus && setStatusDropdownOpen((p) => !p)}
              disabled={statusChanging || !canChangeStatus}
              data-testid="task-status-dropdown"
              title={canChangeStatus ? 'Durum değiştirmek için tıklayın' : 'Bu görevin durumunu değiştirme yetkiniz yok'}
              className={`flex min-h-[48px] items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold uppercase shadow-sm ${getStatusDisplay(task).badge} ${
                canChangeStatus ? 'hover:brightness-95 active:scale-[0.98]' : 'cursor-not-allowed opacity-70'
              }`}
            >
              {statusChanging && <Loader2 className="h-4 w-4 animate-spin" />}
              {getStatusDisplay(task).label}
              {canChangeStatus && <ChevronDown className="h-4 w-4" />}
            </button>
            {canReopenTask && (
            <button
              type="button"
              onClick={handleReopen}
              disabled={statusChanging}
              data-testid="task-reopen-button"
              className="flex min-h-[44px] items-center gap-1 rounded bg-blue-100 px-2 py-1 text-xs font-bold uppercase text-blue-700 hover:bg-blue-200 disabled:opacity-60"
            >
              {statusChanging && <Loader2 className="h-3 w-3 animate-spin" />}
              Yeniden Aç
            </button>
          )}
          {statusDropdownOpen && canChangeStatus && (
              <div className="absolute left-0 z-50 mt-1.5 w-56 max-w-[calc(100vw-1rem)] rounded-lg border border-slate-200 bg-white py-1 shadow-xl sm:left-auto sm:w-60">
                {Object.entries(STATUS_LABELS).filter(([key]) => key !== 'arsivlendi').map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`status-option-${key}`}
                    onClick={() => handleStatusChange(key)}
                    className={`flex min-h-[44px] w-full items-center justify-between px-4 py-2.5 text-left text-sm font-medium hover:bg-slate-50 ${
                      task.status === key ? 'text-blue-600' : 'text-slate-700'
                    }`}
                  >
                    {label}
                    {task.status === key && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className={`rounded px-2 py-0.5 text-xs font-bold uppercase ${PRIORITY_BADGE[task.priority]}`}>
            {PRIORITY_LABELS[task.priority]}
          </span>
          {isOverdue(task) && (
            <span className="flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs font-bold uppercase text-red-700">
              <AlertTriangle className="h-3 w-3" />
              Gecikti
            </span>
          )}
          {isArchived && (
            <span className="flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs font-bold uppercase text-slate-700">
              <Archive className="h-3 w-3" />
              Arşivlendi
            </span>
          )}
        </div>
        <div className="flex items-start justify-between gap-3">
          <h1 className="min-w-0 flex-1 break-words text-base font-bold text-slate-900 sm:text-xl" data-testid="task-detail-title">{task.title}</h1>
          <div className="flex shrink-0 items-center gap-1">
            {task && (
              <button
                onClick={() => setReminderModalOpen(true)}
                data-testid="task-reminder-button"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600 sm:h-11 sm:w-11"
                title="Hatırlatıcı Ekle"
                aria-label="Hatırlatıcı Ekle"
              >
                <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            )}
            {canEditTask && (
              <button
                onClick={() => setEditModalOpen(true)}
                data-testid="task-edit-button"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 sm:h-11 sm:w-11"
                title="Görevi Düzenle"
                aria-label="Görevi Düzenle"
              >
                <Pencil className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            )}
            {canEditTask && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="task-delete-button"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 sm:h-11 sm:w-11"
                title="Görevi Sil"
                aria-label="Görevi Sil"
              >
                <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            )}
          </div>
        </div>
        {task.description && <p className="mt-2 whitespace-pre-wrap text-xs text-slate-600 sm:text-sm">{task.description}</p>}

        <div className="mt-3 grid grid-cols-1 gap-3 sm:mt-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex items-center gap-2 text-xs text-slate-600 sm:text-sm">
            <Link2 className="h-4 w-4 text-slate-500" />
            {projectName ? (
              <button onClick={() => navigate(`/projects`)} data-testid="task-project-link" className="text-blue-600 hover:underline">
                {projectName}
              </button>
            ) : (
              <span className="text-slate-500">Proje atanmamış</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600 sm:text-sm">
            <Info className="h-4 w-4 text-slate-500" />
            {task.creator ? (
              <span data-testid="task-creator" className="text-slate-700">
                Oluşturan: {task.creator.name} {task.creator.surname}
              </span>
            ) : (
              <span className="text-slate-500">Oluşturan bilgisi yok</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600 sm:text-sm">
            <User className="h-4 w-4 text-slate-500" />
            <div className="flex flex-wrap items-center gap-1.5">
              {assignees.length === 0 && task.assigned_groups?.length === 0 && (
                <span className="text-slate-500">Atanmamış</span>
              )}
              <div className="flex -space-x-1.5">
                {assignees.map((u) =>
                  u.avatar ? (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setSelectedAssignee(u)}
                      data-testid={`assignee-avatar-${u.id}`}
                      className="h-5 w-5 rounded-full border border-white object-cover transition-transform hover:z-10 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:h-6 sm:w-6"
                    >
                      <img src={u.avatar} alt="" className="h-full w-full rounded-full object-cover" />
                    </button>
                  ) : (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setSelectedAssignee(u)}
                      data-testid={`assignee-avatar-${u.id}`}
                      className="flex h-5 w-5 items-center justify-center rounded-full border border-white bg-blue-100 text-[9px] font-semibold text-blue-700 transition-transform hover:z-10 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:h-6 sm:w-6 sm:text-[10px]"
                    >
                      {u.name[0]}
                    </button>
                  )
                )}
              </div>
              {task.assigned_groups?.map((g) => (
                <span
                  key={g.id}
                  data-testid={`assigned-group-${g.id}`}
                  className="rounded-full bg-purple-100 px-1.5 py-0 text-[10px] font-medium text-purple-700 sm:px-2 sm:py-0.5 sm:text-xs"
                >
                  {g.name}
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600 sm:text-sm">
            <Calendar className="h-4 w-4 text-slate-500" />
            {task.due_date ? (
              <span className={isOverdue(task) ? 'font-semibold text-red-600' : ''}>
                {new Date(task.due_date + 'Z').toLocaleDateString('tr-TR')}
              </span>
            ) : (
              <span className="text-slate-500">Tarih yok</span>
            )}
          </div>
        </div>
      </div>

      {/* Alt Görevler */}
      {subTasks.length > 0 && (
        <div className="rounded-xl bg-white p-3.5 shadow-sm sm:p-5">
          <h3 className="mb-3 text-xs font-semibold text-slate-900 sm:text-sm">
            Alt Görevler ({completedSubs}/{subTasks.length})
          </h3>
          <div className="space-y-2">
            {subTasks.map((st) => (
              <label key={st.id} data-testid="subtask-item" className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={st.is_completed}
                  onChange={() => toggleSubTask(st.id, st.is_completed)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600"
                />
                <span className={`text-xs ${st.is_completed ? 'text-slate-400 line-through' : 'text-slate-700'} sm:text-sm`}>
                  {st.title}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Sekmeler */}
      <div className="rounded-xl bg-white shadow-sm">
        <div className="flex overflow-x-auto border-b border-slate-200">
          {[
            { key: 'comments', label: 'Yorumlar', icon: MessageSquare },
            { key: 'history', label: 'Geçmiş', icon: Clock },
            { key: 'dependencies', label: 'Bağımlılıklar', icon: Link2 },
            { key: 'files', label: 'Dosyalar', icon: Paperclip },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex min-h-[44px] min-w-[80px] items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition sm:px-4 sm:py-3 sm:text-sm ${
                  activeTab === tab.key
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-3.5 sm:p-5">
          {activeTab === 'comments' && (
            <div className="space-y-4">
              {comments.length === 0 && <p className="text-xs text-slate-500 sm:text-sm">Henüz yorum yok.</p>}
              {comments.map((c) => {
                const author = c.user;
                const isSystem = c.type && c.type.startsWith('system_');

                if (isSystem) {
                  const isApproval = c.type === 'system_approval';
                  const isRejection = c.type === 'system_rejection';
                  const SystemIcon = isApproval ? CheckCircle2 : isRejection ? XCircle : Info;
                  const systemColor = isApproval
                    ? 'bg-green-50 text-green-700'
                    : isRejection
                      ? 'bg-red-50 text-red-700'
                      : 'bg-blue-50 text-blue-700';

                  return (
                    <div key={c.id} className="flex justify-center" data-testid={`system-comment-${c.id}`}>
                      <div className={`inline-flex max-w-[90%] items-center gap-2 rounded-full px-3 py-1.5 text-xs ${systemColor}`}>
                        <SystemIcon className="h-3.5 w-3.5 shrink-0" />
                        <span className="font-medium">{c.comment}</span>
                        <span className="opacity-70">
                          {c.created_at ? new Date(c.created_at + 'Z').toLocaleString('tr-TR', DATE_TIME_FORMAT) : ''}
                        </span>
                      </div>
                    </div>
                  );
                }

                const attachments = c.attachments && c.attachments.length > 0
                  ? c.attachments
                  : c.attachment_path
                    ? [{ path: c.attachment_path, url: c.attachment_url || c.attachment_path, name: 'Ek dosyası' }]
                    : [];
                const commentUserId = c.user_id ?? c.user?.id;
                const canDeleteComment = !c.is_deleted && user && (commentUserId == user.id || user.role === 'admin');
                return (
                  <div key={c.id} className="flex gap-3" data-testid={`user-comment-${c.id}`}>
                    {author?.avatar ? (
                      <img
                        src={author.avatar}
                        alt=""
                        className="h-8 w-8 shrink-0 rounded-full border border-white object-cover sm:h-9 sm:w-9"
                      />
                    ) : (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700 sm:h-9 sm:w-9 sm:text-sm">
                        {author ? `${author.name[0]}${author.surname[0]}` : 'U'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className={`rounded-lg px-3 py-2 ${c.is_deleted ? 'bg-slate-100' : 'bg-slate-50'}`}>
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-800 sm:text-sm">
                            {author ? `${author.name} ${author.surname}` : 'Kullanıcı'}
                          </span>
                          <span className="text-[10px] text-slate-500 sm:text-xs">
                            {c.created_at ? new Date(c.created_at + 'Z').toLocaleString('tr-TR', DATE_TIME_FORMAT) : ''}
                          </span>
                          {canDeleteComment && (
                            <button
                              type="button"
                              onClick={() => setDeleteCommentTarget(c.id)}
                              disabled={deletingComment}
                              title="Yorumu sil"
                              aria-label="Yorumu sil"
                              data-testid={`comment-delete-button-${c.id}`}
                              className="ml-auto rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                            </button>
                          )}
                        </div>
                        {c.comment && (
                          <p className={`break-words whitespace-pre-wrap text-sm sm:text-base ${c.is_deleted ? 'italic text-slate-500' : 'text-slate-800'}`}>
                            {c.comment}
                          </p>
                        )}
                        {!c.is_deleted && attachments.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {attachments.map((att, idx) => {
                              const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)(\?.*)?$/i.test(att.name || att.path || '');
                              const matchedFile = att.path
                                ? files.find((f) => f.current_file_path === att.path || f.path === att.path)
                                : undefined;
                              const openAttachment = () => {
                                if (matchedFile) {
                                  setPreviewFile(matchedFile);
                                } else {
                                  window.open(att.url, '_blank', 'noopener');
                                }
                              };
                              return isImage ? (
                                <div
                                  key={idx}
                                  role="button"
                                  tabIndex={0}
                                  onClick={openAttachment}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAttachment(); } }}
                                  data-testid={`comment-attachment-${c.id}-${idx}`}
                                  className="group relative inline-block cursor-pointer"
                                  title="Önizle"
                                >
                                  <img
                                    src={att.url}
                                    alt={att.name}
                                    className="max-h-36 rounded-lg border border-slate-200 object-cover hover:border-blue-300 sm:max-h-48"
                                  />
                                  {matchedFile && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); downloadTaskFile(matchedFile); }}
                                      data-testid={`comment-attachment-download-${c.id}-${idx}`}
                                      title="İndir"
                                      aria-label="Eki indir"
                                      className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-lg bg-black/50 text-white opacity-80 hover:bg-black/70"
                                    >
                                      <Download className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div
                                  key={idx}
                                  role="button"
                                  tabIndex={0}
                                  onClick={openAttachment}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAttachment(); } }}
                                  data-testid={`comment-attachment-${c.id}-${idx}`}
                                  className="flex min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm"
                                  title="Önizle"
                                >
                                  <FileText className="h-4 w-4 shrink-0 text-slate-500" />
                                  <span className="min-w-0 flex-1 truncate">{att.name}</span>
                                  {matchedFile ? (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); downloadTaskFile(matchedFile); }}
                                      data-testid={`comment-attachment-download-${c.id}-${idx}`}
                                      title="İndir"
                                      aria-label="Eki indir"
                                      className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-600"
                                    >
                                      <Download className="h-4 w-4" />
                                    </button>
                                  ) : (
                                    <Paperclip className="h-4 w-4 shrink-0 text-slate-500" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              <form onSubmit={sendComment} className="space-y-3 pt-2">
                <div className="flex flex-col gap-2">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={handleCommentKeyDown}
                    placeholder="Yorum yazın... (Shift+Enter ile yeni satır)"
                    rows={2}
                    data-testid="comment-input"
                    className="min-h-[4rem] flex-1 resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 sm:text-base"
                  />
                  <div className="flex flex-nowrap gap-2">
                    <label
                      htmlFor="comment-photo-input"
                      className="relative flex min-h-[48px] flex-1 cursor-pointer items-center justify-center gap-1.5 overflow-hidden rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 sm:min-h-[44px] sm:gap-2 sm:px-3 sm:text-sm"
                      title="Fotoğraf ekle"
                      aria-label="Fotoğraf ekle"
                    >
                      <ImageIcon className="h-5 w-5 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">Fotoğraf</span>
                      <span className="sm:hidden">Foto</span>
                      <input
                        id="comment-photo-input"
                        type="file"
                        multiple
                        accept="image/*"
                        data-testid="comment-photo-input"
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        onChange={handleAttachmentSelect}
                      />
                    </label>
                    <label
                      htmlFor="comment-document-input"
                      className="relative flex min-h-[48px] flex-1 cursor-pointer items-center justify-center gap-1.5 overflow-hidden rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 sm:min-h-[44px] sm:gap-2 sm:px-3 sm:text-sm"
                      title="Dosya / belge ekle"
                      aria-label="Dosya / belge ekle"
                    >
                      <FileText className="h-5 w-5 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">Dosya</span>
                      <span className="sm:hidden">Belge</span>
                      <input
                        id="comment-document-input"
                        type="file"
                        multiple
                        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip,application/x-rar-compressed,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                        data-testid="comment-document-input"
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        onChange={handleAttachmentSelect}
                      />
                    </label>
                    <button
                      type="submit"
                      data-testid="comment-submit"
                      disabled={sendingComment || (!commentText.trim() && commentAttachments.length === 0)}
                      className="flex min-h-[48px] flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 sm:min-h-[44px] sm:px-4"
                    >
                      {sendingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Gönder
                    </button>
                  </div>
                </div>
                {commentAttachments.length > 0 && (
                  <div className="space-y-2">
                    {commentAttachments.map((file, idx) => (
                      <div
                        key={`${file.name}-${idx}`}
                        className="flex min-w-0 items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600 sm:text-base"
                      >
                        <Paperclip className="h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1 truncate">{file.name}</span>
                        <span className="shrink-0 text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-600"
                          aria-label="Eki kaldır"
                          title="Eki kaldır"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </form>
            </div>
          )}

          {activeTab === 'files' && (() => {
            const byDateDesc = (a: FileItem, b: FileItem) =>
              toApiDate(b.created_at).getTime() - toApiDate(a.created_at).getTime();
            const images = files.filter((f) => (f.mime_type || '').startsWith('image/')).sort(byDateDesc);
            const docs = files.filter((f) => !(f.mime_type || '').startsWith('image/')).sort(byDateDesc);

            if (files.length === 0) {
              return (
                <p className="text-xs text-slate-500 sm:text-sm" data-testid="task-files-empty">
                  Henüz dosya veya görüntü paylaşılmadı.
                </p>
              );
            }

            return (
              <div className="space-y-5" data-testid="task-files-panel">
                {images.length > 0 && (
                  <div>
                    <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase text-slate-500">
                      <ImageIcon className="h-3.5 w-3.5" /> Görüntüler ({images.length})
                    </h4>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {images.map((f) => (
                        <SharedImageThumb key={f.id} file={f} onOpen={setPreviewFile} />
                      ))}
                    </div>
                  </div>
                )}
                {docs.length > 0 && (
                  <div>
                    <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase text-slate-500">
                      <FileText className="h-3.5 w-3.5" /> Dosyalar ({docs.length})
                    </h4>
                    <div className="space-y-2">
                      {docs.map((f) => (
                        <div
                          key={f.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewFile(f)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewFile(f); } }}
                          data-testid={`shared-file-${f.id}`}
                          className="flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm"
                        >
                          <FileText className="h-4 w-4 shrink-0 text-slate-500" />
                          <span className="min-w-0 flex-1 truncate">{f.original_name || f.name}</span>
                          {f.owner?.name && <span className="hidden shrink-0 text-slate-500 sm:inline">{f.owner.name}</span>}
                          <span className="shrink-0 text-[10px] text-slate-500 sm:text-xs">
                            {f.created_at ? toApiDate(f.created_at).toLocaleString('tr-TR', DATE_TIME_FORMAT) : ''}
                          </span>
                          {f.size ? (
                            <span className="hidden shrink-0 text-slate-500 sm:inline">{(f.size / 1024).toFixed(1)} KB</span>
                          ) : null}
                          <button
                            type="button"
                            data-testid={`shared-file-download-${f.id}`}
                            onClick={(e) => { e.stopPropagation(); downloadTaskFile(f); }}
                            className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-600"
                            title="İndir"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {activeTab === 'history' && (
            <div className="space-y-3">
              {auditLogs.length === 0 && <p className="text-xs text-slate-500 sm:text-sm">Henüz geçmiş kaydı yok.</p>}
              {auditLogs.map((log) => (
                <div key={log.id} data-testid={`history-item-${log.id}`} className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-4 w-4 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-700 sm:text-sm">
                      <span className="font-medium">{log.user_name}</span>{' '}
                      <span className="text-slate-500">{log.action}</span>
                    </p>
                    <p className="text-xs text-slate-500">{log.created_at ? new Date(log.created_at + 'Z').toLocaleString('tr-TR') : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'dependencies' && (
            <div className="space-y-5" data-testid="task-dependencies-panel">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase text-slate-500">Bu görevin bağlı olduğu görevler</h4>
                {canEditTask && (
                  <button
                    type="button"
                    onClick={openDependencyModal}
                    data-testid="add-dependency-button"
                    className="flex min-h-[44px] items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                  >
                    <Plus className="h-3.5 w-3.5" /> Bağımlılık Ekle
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {dependencies.length === 0 && <p className="text-sm text-slate-500" data-testid="no-dependencies-message">Bağımlı görev yok.</p>}
                {dependencies.map((dep) => {
                  const depTask = dep.depends_on_task;
                  if (!depTask) return null;
                  return (
                    <div
                      key={dep.id}
                      data-testid={`dependency-item-${dep.id}`}
                      className="flex min-h-[44px] w-full items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 sm:gap-3 sm:px-3 sm:py-2"
                    >
                      <button
                        type="button"
                        onClick={() => navigate(`/tasks/${depTask.id}`)}
                        className="flex flex-1 items-center gap-3 text-left"
                      >
                        {depTask.status === 'tamamlandi' ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 sm:h-4 sm:w-4" />
                        ) : (
                          <Circle className="h-3.5 w-3.5 text-slate-500 sm:h-4 sm:w-4" />
                        )}
                        <span className="truncate text-xs text-slate-700 sm:text-sm">{depTask.title}</span>
                      </button>
                      <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-700">
                        {DEPENDENCY_TYPE_LABELS[dep.type] || dep.type}
                      </span>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${STATUS_BADGE[depTask.status] || 'bg-slate-100'}`}>
                        {STATUS_LABELS[depTask.status] || depTask.status}
                      </span>
                      {canEditTask && (
                        <button
                          type="button"
                          onClick={() => removeDependency(dep.id)}
                          data-testid={`remove-dependency-${dep.id}`}
                          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded p-2 text-slate-500 hover:bg-red-100 hover:text-red-600 sm:p-1"
                          title="Bağımlılığı kaldır"
                          aria-label="Bağımlılığı kaldır"
                        >
                          <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-semibold uppercase text-slate-500">Bu göreve bağlı görevler</h4>
                {dependents.length === 0 && <p className="text-sm text-slate-500" data-testid="no-dependents-message">Bu göreve bağlı görev yok.</p>}
                {dependents.map((dep) => {
                  const depTask = dep.task;
                  if (!depTask) return null;
                  return (
                    <div
                      key={dep.id}
                      data-testid={`dependent-item-${dep.id}`}
                      className="flex min-h-[44px] w-full items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 sm:gap-3 sm:px-3 sm:py-2"
                    >
                      <button
                        type="button"
                        onClick={() => navigate(`/tasks/${depTask.id}`)}
                        className="flex flex-1 items-center gap-3 text-left"
                      >
                        {depTask.status === 'tamamlandi' ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 sm:h-4 sm:w-4" />
                        ) : (
                          <Circle className="h-3.5 w-3.5 text-slate-500 sm:h-4 sm:w-4" />
                        )}
                        <span className="truncate text-xs text-slate-700 sm:text-sm">{depTask.title}</span>
                      </button>
                      <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-700">
                        {DEPENDENCY_TYPE_LABELS[dep.type] || dep.type}
                      </span>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${STATUS_BADGE[depTask.status] || 'bg-slate-100'}`}>
                        {STATUS_LABELS[depTask.status] || depTask.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Task Modal */}
      {editModalOpen && task && (
        <TaskModal
          onClose={() => setEditModalOpen(false)}
          onCreated={() => {
            setEditModalOpen(false);
            fetchAll();
          }}
          users={users}
          groups={groups}
          editTask={task}
        />
      )}

      {/* Reminder Modal */}
      {reminderModalOpen && task && (
        <ReminderModal
          open={reminderModalOpen}
          onClose={() => setReminderModalOpen(false)}
          onSaved={() => {
            setReminderModalOpen(false);
          }}
          remindableType="task"
          remindableId={task.id}
          remindableTitle={task.title}
          eligibleRecipients={eligibleReminderRecipients}
        />
      )}

      {/* Delete Confirm Modal */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Görevi Sil"
        description={`"${task.title}" görevini silmek istediğinize emin misiniz?`}
        variant="danger"
        confirmLabel="Sil"
        loading={deleting}
        onConfirm={handleDeleteTask}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Assignee Detail Modal */}
      {selectedAssignee && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-2 pt-2 sm:items-center sm:p-3 sm:py-4"
          data-testid="assignee-detail-modal"
          onClick={(e) => {
            if (e.currentTarget === e.target) setSelectedAssignee(null);
          }}
        >
          <div className="w-full max-w-none rounded-t-xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-xl sm:max-w-sm sm:rounded-xl sm:p-5">
            <div className="mb-4 flex items-center gap-3">
              {selectedAssignee.avatar ? (
                <img
                  src={selectedAssignee.avatar}
                  alt=""
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-lg font-semibold text-blue-700">
                  {selectedAssignee.name[0]}
                </div>
              )}
              <div>
                <h3 className="text-base font-semibold text-slate-900 sm:text-lg">
                  {selectedAssignee.name} {selectedAssignee.surname}
                </h3>
                <p className="text-xs text-slate-500 sm:text-sm">{ROLE_LABELS[selectedAssignee.role] || selectedAssignee.role}</p>
              </div>
            </div>

            <div className="space-y-3 text-sm text-slate-700">
              <div>
                <span className="text-xs font-medium text-slate-500">E-posta</span>
                <p className="break-all">{selectedAssignee.email}</p>
              </div>
              {selectedAssignee.phone && (
                <div>
                  <span className="text-xs font-medium text-slate-500">Telefon</span>
                  <p>{selectedAssignee.phone}</p>
                </div>
              )}
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setSelectedAssignee(null)}
                data-testid="assignee-detail-close"
                className="min-h-[44px] rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Dependency Modal */}
      {dependencyModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-2 pt-2 sm:items-center sm:p-3 sm:py-4"
          onClick={(e) => {
            if (e.currentTarget === e.target) setDependencyModalOpen(false);
          }}
        >
          <div className="w-full max-w-none rounded-t-xl bg-white p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-xl sm:max-w-md sm:rounded-xl sm:p-6 max-h-[85vh] overflow-y-auto" data-testid="add-dependency-modal">
            <h3 className="mb-1 text-base font-semibold text-slate-900 sm:text-lg">Bağımlılık Ekle</h3>
            <p className="mb-3 text-xs text-slate-500 sm:mb-4 sm:text-sm">Bu görevin tamamlanması için hangi göreve ihtiyaç duyduğunu seçin.</p>

            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700 sm:text-sm">Bağımlı Olunan Görev</label>
                <select
                  value={selectedDependencyTaskId}
                  onChange={(e) => setSelectedDependencyTaskId(e.target.value)}
                  data-testid="dependency-task-select"
                  className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 sm:px-3 sm:py-2 sm:text-sm"
                >
                  <option value="">Görev seçin</option>
                  {availableTasks.map((t) => (
                    <option key={t.id} value={t.id}>
                      #{t.id} {t.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700 sm:text-sm">Bağımlılık Türü</label>
                <select
                  value={selectedDependencyType}
                  onChange={(e) => setSelectedDependencyType(e.target.value)}
                  data-testid="dependency-type-select"
                  className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 sm:px-3 sm:py-2 sm:text-sm"
                >
                  <option value="finish-to-start">{DEPENDENCY_TYPE_LABELS['finish-to-start']}</option>
                  <option value="start-to-start">{DEPENDENCY_TYPE_LABELS['start-to-start']}</option>
                  <option value="finish-to-finish">{DEPENDENCY_TYPE_LABELS['finish-to-finish']}</option>
                  <option value="start-to-finish">{DEPENDENCY_TYPE_LABELS['start-to-finish']}</option>
                </select>
              </div>

              {dependencyError && (
                <div className="rounded-lg bg-red-50 p-2.5 text-xs text-red-600 sm:p-3 sm:text-sm" data-testid="dependency-error">
                  {dependencyError}
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-col-reverse gap-2 sm:mt-6 sm:flex-row sm:justify-end">
              <button
                onClick={() => setDependencyModalOpen(false)}
                data-testid="dependency-cancel-button"
                className="min-h-[44px] rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:px-4"
              >
                İptal
              </button>
              <button
                onClick={addDependency}
                disabled={!selectedDependencyTaskId || addingDependency}
                data-testid="dependency-save-button"
                className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 sm:px-4"
              >
                {addingDependency && <Loader2 className="h-4 w-4 animate-spin" />}
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showRejectDialog}
        title="Görevi Reddet"
        description="Görevi reddetme sebebini yazın (isteğe bağlı):"
        variant="danger"
        confirmLabel="Reddet"
        loading={statusChanging}
        onConfirm={confirmReject}
        onCancel={() => setShowRejectDialog(false)}
      >
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={3}
          className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
          placeholder="Reddetme sebebi..."
        />
        {rejectError && (
          <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="reject-error">
            {rejectError}
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={deleteCommentTarget !== null}
        title="Yorumu Sil"
        description="Bu yorumu silmek istediğinize emin misiniz?"
        variant="danger"
        confirmLabel="Sil"
        loading={deletingComment}
        onConfirm={() => {
          if (deleteCommentTarget !== null) void handleDeleteComment(deleteCommentTarget);
        }}
        onCancel={() => setDeleteCommentTarget(null)}
      />

      {previewFile && (
        <FilePreview file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
    </div>
  );
}
